/**
 * Defines the structure for all WebSocket events in the system.
 */
export type WebSocketEvent =
  | { event: 'refresh-ui'; data: { sessionId?: string } }
  | { event: 'plan-ready'; data: { sessionId: string; sessionInputId: string } }
  | {
      event: 'system-prompt-update';
      data: { systemPrompt: string };
    }
  | { event: 'session-created'; data: { sessionId: string } }
  | {
      event: 'llm-stream-chunk';
      data: { sessionInputId: string; chunk: string; isFirst: boolean };
    }
  | {
      event: 'llm-thought-chunk';
      data: { sessionInputId: string; chunk: string; isFirst: boolean };
    }
  | {
      event: 'llm-generation-started';
      data: { sessionInputId: string; sessionId: string; isStreaming: boolean };
    }
  | { event: 'llm-generation-in-progress'; data: { sessionInputId: string } }
  | {
      event: 'llm-generation-ended';
      data: { sessionInputId: string; sessionId: string; error?: string };
    }
  | {
      event: 'llm-retry-attempt';
      data: {
        sessionInputId: string;
        sessionId: string;
        attempt: number;
        maxAttempts: number;
      };
    }
  | {
      event: 'mcp-marketplace-connected';
      data: { marketplaceId: string; serverName: string };
    }
  | {
      event: 'mcp-marketplace-error';
      data: { marketplaceId: string; error: string };
    }
  | {
      event: 'mcp-token-expired';
      data: { serverName: string; marketplaceId?: string | null };
    };
export type WebSocketEventName = WebSocketEvent['event'];

/**
 * A type guard to check if a parsed JSON object is a valid WebSocketEvent.
 * @param obj The object to check.
 * @returns True if the object is a valid WebSocketEvent, false otherwise.
 */
export function isWebSocketEvent(obj: unknown): obj is WebSocketEvent {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  const { event, data } = obj as { event: string; data: unknown };
  if (typeof event !== 'string' || typeof data !== 'object') {
    return false;
  }

  switch (event) {
    case 'refresh-ui':
      return true; // data can be {} or { sessionId: '...' }
    case 'plan-ready':
      return data !== null && 'sessionId' in data && 'sessionInputId' in data;
    case 'system-prompt-update':
      return data !== null && 'systemPrompt' in data;
    case 'session-created':
      return data !== null && 'sessionId' in data;
    case 'llm-stream-chunk':
      return (
        data !== null &&
        'sessionInputId' in data &&
        'chunk' in data &&
        'isFirst' in data
      );
    case 'llm-thought-chunk':
      return (
        data !== null &&
        'sessionInputId' in data &&
        'chunk' in data &&
        'isFirst' in data
      );
    case 'llm-generation-started':
      return (
        data !== null &&
        'sessionInputId' in data &&
        'sessionId' in data &&
        'isStreaming' in data
      );
    case 'llm-generation-in-progress':
      return data !== null && 'sessionInputId' in data;
    case 'llm-generation-ended':
      return (
        data !== null &&
        'sessionInputId' in data &&
        'sessionId' in data
      );
    case 'llm-retry-attempt':
      return (
        data !== null &&
        'sessionInputId' in data &&
        'sessionId' in data &&
        'attempt' in data &&
        'maxAttempts' in data
      );
    case 'mcp-marketplace-connected':
      return (
        data !== null && 'marketplaceId' in data && 'serverName' in data
      );
    case 'mcp-marketplace-error':
      return data !== null && 'marketplaceId' in data && 'error' in data;
    case 'mcp-token-expired':
      return data !== null && 'serverName' in data;
    default:
      return false;
  }
}